import { get, omit } from "lodash";
import { DateTime } from "luxon";
import React, { FC } from "react";

import { Card, CardContent, Skeleton } from "@mui/material";
import Alert from "@mui/material/Alert";
import Typography from "@mui/material/Typography";
import {
  AllSeriesType,
  BarPlot,
  ChartContainer,
  ChartsTooltip,
  ChartsXAxis,
  ChartsYAxis,
} from "@mui/x-charts";

import useTimePeriod from "../../../hooks/use-time-period";
import { useSelector } from "../../../store";
import {
  GetDistributionTimeSeriesApiArg,
  useGetDistributionSumQuery,
} from "../../../store/pomeriumApi";
import {
  formatDurationMS,
  formatInteger,
  formatPercent,
} from "../../../utils/format";

const RequestDurationPercent: FC = () => {
  const { period } = useTimePeriod();
  const { organizationId, clusterId } = useSelector((state) => state.current);
  const { routeId } = useSelector((state) => state.metrics);
  const now = DateTime.now().startOf("minute");
  const start = now.minus(period);
  let params: GetDistributionTimeSeriesApiArg = {
    organizationId,
    clusterId,
    startTime: start.toISO(),
    endTime: now.toISO(),
    routeId: routeId,
    metricId: "upstream_request_time",
    percentile: 99,
  };
  if (!routeId) {
    params = omit(params, ["routeId"]);
  }

  const { data: authReqs } = useGetDistributionSumQuery(params, {
    skip: !organizationId || !clusterId,
  });

  const counts: number[] = get(
    authReqs,
    "0.distributionValue.bucketCounts",
    [],
  );
  const xAxis: number[] = get(
    authReqs,
    "0.distributionValue.explicitBucketBounds",
    [],
  ).slice(0, counts.length);
  const total = get(authReqs, "0.distributionValue.count", 0);

  return (
    <Card
      sx={{
        width: "100%",
        m: 2,
      }}
    >
      <CardContent sx={{ height: "40vh", m: 2 }}>
        <Typography variant={"h4"} color="text.primary" gutterBottom>
          Request Duration
        </Typography>
        {!authReqs && (
          <Skeleton width="100%" height="100%" sx={{ transform: "unset" }} />
        )}
        {authReqs && !counts?.length && (
          <Alert severity="info">
            No data to display. Data is updated hourly.
          </Alert>
        )}
        {!!counts?.length && (
          <ChartContainer
            yAxis={[
              {
                id: "request-duration-percent-y",
                label: "Number of Requests",
              },
            ]}
            xAxis={[
              {
                id: "request-duration-percent-x",
                scaleType: "band",
                data: xAxis,
                valueFormatter: (value: number) => formatDurationMS(value),
              },
            ]}
            series={[
              {
                id: "request-duration-percent",
                type: "bar",
                data: counts,
                valueFormatter: (value: number) => {
                  if (value === null) {
                    return "";
                  }
                  return (
                    formatInteger(value) +
                    " requests (" +
                    formatPercent(value, total) +
                    ")"
                  );
                },
              } as AllSeriesType<"bar">,
            ]}
          >
            <ChartsTooltip trigger="axis" />
            <BarPlot />
            <ChartsXAxis
              label="Duration in Milliseconds"
              axisId="request-duration-percent-x"
            />
            <ChartsYAxis axisId={"request-duration-percent-y"} />
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default RequestDurationPercent;
